// Mein-Tab — Portfolio + Offene Trades + Aktive RAS-Regeln.
// Liest aus Phase-1-Hooks (Portfolio + Trading-Log + EVE-Memory).

import { useEveMemory } from '@/hooks/use-eve-memory';
import { useEvePortfolio } from '@/hooks/use-eve-portfolio';
import { useTradingLog } from '@/hooks/use-trading-log';

export function TabMein() {
  const { portfolio } = useEvePortfolio();
  const { openTrades } = useTradingLog();
  const { rasRules } = useEveMemory();

  const pnlToday = computePnlToday(openTrades);

  return (
    <div className="space-y-3">
      <PortfolioCard
        currentCapital={portfolio.currentCapital}
        startCapital={portfolio.startCapital}
        pnlToday={pnlToday}
      />
      <OpenTradesCard openTrades={openTrades} />
      <RasCard rules={rasRules} />
    </div>
  );
}

interface PortfolioCardProps {
  currentCapital: number;
  startCapital: number;
  pnlToday: number | null;
}

function PortfolioCard({ currentCapital, startCapital, pnlToday }: PortfolioCardProps) {
  const totalPct = startCapital > 0 ? ((currentCapital - startCapital) / startCapital) * 100 : 0;
  return (
    <div className="glass rounded-xl p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[12px] uppercase tracking-wider text-neutral-500">Portfolio</span>
        {pnlToday != null ? (
          <span
            className={`text-[11px] tabular ${pnlToday >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}
          >
            {pnlToday >= 0 ? '+' : ''}${pnlToday.toFixed(0)} heute
          </span>
        ) : null}
      </div>
      <div className="mb-0.5 text-2xl font-semibold tabular">
        ${currentCapital.toLocaleString('en-US', { maximumFractionDigits: 0 })}
      </div>
      <div className="text-[12px] text-neutral-500">
        Start ${startCapital.toLocaleString('en-US', { maximumFractionDigits: 0 })} ·{' '}
        <span className={totalPct >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
          {totalPct >= 0 ? '+' : ''}
          {totalPct.toFixed(2)}%
        </span>{' '}
        gesamt
      </div>
    </div>
  );
}

interface OpenTrade {
  id: string;
  asset: string;
  direction: 'LONG' | 'SHORT' | string;
  entry: number;
  pnlAbsolute: number | null;
}

function OpenTradesCard({ openTrades }: { openTrades: ReadonlyArray<OpenTrade> }) {
  return (
    <div className="glass rounded-xl p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[12px] uppercase tracking-wider text-neutral-500">
          Offene Trades · {openTrades.length}
        </span>
      </div>
      {openTrades.length === 0 ? (
        <p className="py-1 text-center text-[11px] text-neutral-500 italic">Keine offenen Trades.</p>
      ) : (
        <div className="space-y-1.5 text-xs">
          {openTrades.slice(0, 6).map((t) => {
            const pnl = t.pnlAbsolute;
            const isUp = (pnl ?? 0) >= 0;
            return (
              <div key={t.id} className="flex justify-between">
                <span className={`font-mono ${isUp ? 'text-emerald-300' : 'text-rose-300'}`}>
                  {t.asset} {t.direction}
                </span>
                <span className={`tabular ${isUp ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {pnl == null ? '—' : `${pnl >= 0 ? '+' : ''}$${pnl.toFixed(0)}`}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface RasRule {
  id: number | string;
  rule: string;
}

function RasCard({ rules }: { rules: ReadonlyArray<RasRule> }) {
  return (
    <div className="glass rounded-xl p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[12px] uppercase tracking-wider text-neutral-500">Aktive RAS</span>
        <span className="text-[11px] text-neutral-500">{rules.length}</span>
      </div>
      {rules.length === 0 ? (
        <p className="text-[11px] text-neutral-500 italic">
          Noch keine RAS-Regeln. EVE lernt im Gespräch.
        </p>
      ) : (
        <div className="space-y-1 text-[12px] text-neutral-400">
          {rules.slice(0, 3).map((r) => (
            <div key={r.id}>• {truncate(r.rule, 60)}</div>
          ))}
          {rules.length > 3 ? (
            <button className="text-amber-300 hover:underline">
              alle {rules.length} ansehen →
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

function computePnlToday(openTrades: ReadonlyArray<OpenTrade>): number | null {
  if (openTrades.length === 0) return null;
  const sum = openTrades.reduce((acc, t) => acc + (t.pnlAbsolute ?? 0), 0);
  return sum;
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}
